import * as LitComps from "./LitIconButton";
import Icon, { SlotIconName } from "../Icon";
import type { MaterialNames } from "../material_symbol_names";
import { SlotIcon } from "../Icon";
import classNames from "classnames";
import { useRef, useEffect } from "react";
import { updateFocusRing } from "@/lib/styleHelpers";

const variants = {
  outlined: LitComps.LitOutlinedIconButton,
  filled: LitComps.LitFilledIconButton,
  filledTonal: LitComps.LitFilledTonalIconButton,
  square: LitComps.LitOutlinedIconButton,
  default: LitComps.LitIconButton
};

export interface Props {
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
  iconClassName?: string;
  name: MaterialNames;
  ariaLabel?: string;
  onClick?: (e: any) => void;
  selected?: boolean;
  selectedIcon?: SlotIconName;
  skipMargin?: boolean;
  title?: string;
  variant?: keyof typeof variants;
  "data-testid"?: string;
  id?: string;
  tabIndex?: number;
  trailingIcon?: boolean;
  type?: "button" | "submit" | "reset";
}

// IconButtons are supposed to be 48x48px on Figma. The material web library renders it as 40x40px with an extra 8x8px touchable area which is not computed during render.
// Adding m-2 to the IconButton to make it 48x48px.
const defaultMargin = "m-2";

const customStyles = {
  "--md-focus-ring-width": "-10"
} as React.CSSProperties;

const squareStyles = {
  "--md-icon-button-state-layer-size": "100%",
  "--md-icon-button-state-layer-shape": "4px",
  "--md-icon-button-state-layer-color": "var(--md-sys-color-on-surface-variant)",
  "--md-icon-button-hover-state-layer-opacity": "0.04",
  "--md-icon-button-pressed-state-layer-opacity": "0.08",
  "--md-ripple-hover-opacity": "0",
  "--md-ripple-pressed-opacity": "0",
  "--md-ripple-hover-color": "transparent",
  "--md-ripple-pressed-color": "transparent",
  "--md-outlined-icon-button-outline-width": "1px",
  "--md-outlined-icon-button-container-shape": "4px",
  "--md-outlined-icon-button-container-height": "40px",
  "--md-outlined-icon-button-container-width": "40px"
} as React.CSSProperties;

export default function IconButton({
  className,
  containerClassName,
  iconClassName,
  onClick,
  disabled,
  selected,
  name,
  title = name,
  ariaLabel = title,
  selectedIcon,
  skipMargin,
  variant = "default",
  "data-testid": testId,
  trailingIcon,
  type,
  id,
  tabIndex = 0
}: Props) {
  const ButtonComponent = variants[variant];

  const iconButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => updateFocusRing(iconButtonRef, "md-icon-button"), 100);
  }, []);

  const componentStyle = {
    ...customStyles,
    ...(variant === "square" ? squareStyles : {}),
    ...(variant === "square" && {
      "--md-outlined-icon-button-outline-color": disabled
        ? "var(--md-sys-color-outline-variant)"
        : "var(--md-sys-color-outline)"
    })
  };

  return (
    <div className={classNames("inline-block", containerClassName)} ref={iconButtonRef}>
      <ButtonComponent
        {...{ title, disabled, onClick, selected, id, tabIndex }}
        {...(type ? { type } : {})}
        className={classNames(className, { [defaultMargin]: !skipMargin && variant !== "square" })}
        aria-label={ariaLabel}
        data-testid={testId}
        style={componentStyle}
      >
        {trailingIcon && <SlotIcon name={name} slot="icon" />}
        <Icon
          name={name}
          className={iconClassName}
          weight={name === "more_vert" ? 800 : 400}
          size={name === "open_in_full" ? 20 : undefined}
        />
        {!trailingIcon && selectedIcon && <SlotIcon name={selectedIcon} slot="selected" />}
      </ButtonComponent>
    </div>
  );
}
